import { logger } from "@/lib/logger";

/* eslint-disable no-bitwise */
export const JWT_SECRET = "jwt-secret-key"; // pragma: allowlist secret
export const JWT_EXPIRES_IN = 3600 * 24 * 2;

// Helper function for URL-safe Base64 encoding
const urlBase64Encode = (str: string): string => {
  return btoa(encodeURIComponent(str).replace(/%([0-9A-F]{2})/g, (_match, p1) => String.fromCharCode(parseInt(p1, 16))))
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/, "");
};

// Helper function for URL-safe Base64 decoding
const urlBase64Decode = (str: string): string => {
  // Add padding if needed
  let output = str.replace(/-/g, "+").replace(/_/g, "/");
  const pad = output.length % 4;
  if (pad) {
    if (pad === 1) {
      throw new Error("Invalid base64 string");
    }
    output += new Array(5 - pad).join("=");
  }

  try {
    return decodeURIComponent(
      Array.prototype.map
        .call(atob(output), (c: string) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
        .join("")
    );
  } catch (e) {
    // Fallback if decodeURIComponent fails
    return atob(output);
  }
};

export const sign = (payload: Record<string, any>, privateKey: string, header: Record<string, any>) => {
  const now = new Date();
  header.expiresIn = new Date(now.getTime() + header.expiresIn);

  const encodedHeader = urlBase64Encode(JSON.stringify(header));
  const encodedPayload = urlBase64Encode(JSON.stringify(payload));

  const signature = urlBase64Encode(
    Array.from(encodedPayload)
      .map((item, key) => String.fromCharCode(item.charCodeAt(0) ^ privateKey[key % privateKey.length].charCodeAt(0)))
      .join("")
  );

  return `${encodedHeader}.${encodedPayload}.${signature}`;
};

export const decode = (token: string): any => {
  const [encodedHeader, encodedPayload] = token.split(".");

  try {
    const header = JSON.parse(urlBase64Decode(encodedHeader));
    const payload = JSON.parse(urlBase64Decode(encodedPayload));
    const now = new Date();

    if (now > new Date(header.expiresIn)) {
      throw new Error("Expired token");
    }

    return payload;
  } catch (e) {
    logger.error("Error decoding token:", e);
    throw new Error("Invalid token");
  }
};

export const verify = (token: string, privateKey: string): Record<string, any> => {
  const [encodedHeader, encodedPayload, signature] = token.split(".");

  const header = JSON.parse(urlBase64Decode(encodedHeader));
  const payload = JSON.parse(urlBase64Decode(encodedPayload));
  const now = new Date();

  if (now > new Date(header.expiresIn)) {
    throw new Error("The token is expired!");
  }

  const verifiedSignature = urlBase64Encode(
    Array.from(encodedPayload)
      .map((item, key) => String.fromCharCode(item.charCodeAt(0) ^ privateKey[key % privateKey.length].charCodeAt(0)))
      .join("")
  );

  if (verifiedSignature !== signature) {
    throw new Error("The signature is invalid!");
  }

  return payload;
};
